import React, { useState, useEffect, useCallback } from 'react'
import { fetchWithAuth } from '../../utils/apiClient'
import ReauthModal from './ReauthModal'
import useGlobalTheme from '../../hooks/useGlobalTheme'

function SecuritySettings() {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
	const [toggling2FA, setToggling2FA] = useState(false)
	const [hasPassword, setHasPassword] = useState(true)
	const [showPasswordForm, setShowPasswordForm] = useState(false)
	const [currentPassword, setCurrentPassword] = useState('')
	const [newPassword, setNewPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [passwordLoading, setPasswordLoading] = useState(false)
	const [passwordSuccess, setPasswordSuccess] = useState('')
	const [error, setError] = useState('')
	const [pendingAction, setPendingAction] = useState(null)

	useEffect(() => {
		loadSecuritySettings()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (!error) return
		const timer = setTimeout(() => setError(''), 15000)
		return () => clearTimeout(timer)
	}, [error])

	useEffect(() => {
		if (!passwordSuccess) return
		const timer = setTimeout(() => setPasswordSuccess(''), 15000)
		return () => clearTimeout(timer)
	}, [passwordSuccess])

	const loadSecuritySettings = async () => {
		try {
			const res = await fetchWithAuth('/api/user/security-settings')
			const data = await res.json()
			setTwoFactorEnabled(data.twoFactorEnabled || false)
			setHasPassword(data.hasPassword || false)
		} catch {
			// defaults
		}
	}

	const requireReauth = useCallback(action => {
		setPendingAction(() => action)
	}, [])

	const handleReauthVerified = useCallback(
		async credentials => {
			const action = pendingAction
			setPendingAction(null)
			if (action) {
				try {
					await action(credentials)
				} catch (err) {
					const errorMsg = err.message || 'Operation failed'
					setError(errorMsg)
				}
			}
		},
		[pendingAction]
	)

	const handleReauthCancel = useCallback(() => {
		setPendingAction(null)
		setToggling2FA(false)
	}, [])

	const handleToggle2FA = () => {
		setError('')

		if (twoFactorEnabled) {
			requireReauth(async credentials => {
				setToggling2FA(true)
				try {
					const res = await fetchWithAuth('/api/user/security-settings', {
						method: 'PUT',
						headers: {
							'Content-Type': 'application/json',
						},
						body: JSON.stringify({ twoFactorEnabled: false, ...credentials }),
					})
					const data = await res.json()
					if (res.ok) {
						setTwoFactorEnabled(data.twoFactorEnabled)
					} else {
						setError(data.error || 'Failed to update 2FA setting')
					}
				} finally {
					setToggling2FA(false)
				}
			})
		} else {
			toggleWithoutReauth()
		}
	}

	const toggleWithoutReauth = async () => {
		setToggling2FA(true)
		try {
			const res = await fetchWithAuth('/api/user/security-settings', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ twoFactorEnabled: true }),
			})
			const data = await res.json()
			if (res.ok) {
				setTwoFactorEnabled(data.twoFactorEnabled)
			} else {
				setError(data.error || 'Failed to update 2FA setting')
			}
		} catch {
			setError('Failed to update 2FA setting')
		} finally {
			setToggling2FA(false)
		}
	}

	const submitPasswordChange = async (reauthCredentials = {}) => {
		setPasswordLoading(true)
		try {
			const res = await fetchWithAuth('/api/user/change-password', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					currentPassword: currentPassword || undefined,
					newPassword,
					confirmPassword,
					...reauthCredentials,
				}),
			})

			const data = await res.json()
			if (res.ok) {
				setPasswordSuccess('Password updated successfully')
				resetPasswordForm()
				setHasPassword(true)
			} else {
				setError(data.error || 'Failed to change password')
			}
		} catch {
			setError('Failed to change password')
		} finally {
			setPasswordLoading(false)
		}
	}

	const handleChangePassword = async () => {
		setError('')
		setPasswordSuccess('')

		if (!newPassword.trim() || !confirmPassword.trim()) {
			return setError('Please fill in all password fields')
		}
		if (newPassword.trim().length < 8) {
			return setError('New password must be at least 8 characters')
		}
		if (newPassword.trim() !== confirmPassword.trim()) {
			return setError('Passwords do not match')
		}

		if (!hasPassword) {
			requireReauth(async (credentials) => {
				await submitPasswordChange(credentials)
			})
			return
		}

		await submitPasswordChange()
	}

	const resetPasswordForm = () => {
		setShowPasswordForm(false)
		setCurrentPassword('')
		setNewPassword('')
		setConfirmPassword('')
	}

	return (
		<div>
			<h2 className={`text-2xl font-bold mb-6 ${isLight ? 'text-gray-900' : 'text-white'}`}>Security</h2>

			{error && (
				<div className={`rounded-lg p-3 mb-4 text-sm border ${
					isLight
						? 'bg-red-50 text-red-700 border-red-200'
						: 'bg-red-900/30 text-red-300 border-red-800'
				}`}>
					{error}
					<button
						onClick={() => setError('')}
						className={`float-right ${isLight ? 'text-red-600 hover:text-red-700' : 'text-red-400 hover:text-red-300'}`}
					>
						&times;
					</button>
				</div>
			)}

			{/* Two-Factor Authentication */}
			<div className={`border rounded-lg p-5 mb-6 ${isLight ? 'border-gray-200' : 'border-gray-600'}`}>
				<div className="flex items-center justify-between mb-2">
					<h3 className={`text-base font-medium ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>
						Email Two-Factor Authentication
					</h3>
					<button
						onClick={handleToggle2FA}
						disabled={toggling2FA}
						className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
							twoFactorEnabled
								? 'bg-blue-600'
								: (isLight ? 'bg-gray-300' : 'bg-gray-500')
						}`}
					>
						<span
							className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
								twoFactorEnabled ? 'translate-x-6' : 'translate-x-1'
							}`}
						/>
					</button>
				</div>
				<p className={`text-sm ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
					When enabled, you'll receive a verification code via email when logging in
					from a new device or browser.
				</p>
			</div>

			{/* Password */}
			<div className={`border rounded-lg p-5 mb-6 ${isLight ? 'border-gray-200' : 'border-gray-600'}`}>
				<div className="flex items-center justify-between">
					<div>
						<h3 className={`text-base font-medium ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>Password</h3>
						<p className={`text-sm mt-1 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
							{hasPassword
								? 'Update your password to keep your account secure.'
								: 'Set a password to enable email/password login.'}
						</p>
					</div>
					<button
						onClick={() => {
							setError('')
							setPasswordSuccess('')
							setShowPasswordForm(true)
						}}
						className={`text-sm font-medium ${isLight ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400 hover:text-blue-300'}`}
					>
						{hasPassword ? 'Change password' : 'Set password'}
					</button>
				</div>

				{passwordSuccess && (
					<div className={`rounded-lg p-3 mt-3 text-sm border ${
						isLight
							? 'bg-green-50 text-green-700 border-green-200'
							: 'bg-green-900/30 text-green-300 border-green-800'
					}`}>
						{passwordSuccess}
					</div>
				)}
			</div>

			{showPasswordForm && (
				<div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 pt-20 p-4">
					<div className={`rounded-lg w-full max-w-lg mx-4 overflow-hidden ${isLight ? 'bg-white' : 'bg-[#1e2a3a]'}`}>
						<form onSubmit={e => { e.preventDefault(); handleChangePassword() }} autoComplete="off">
							<div className="p-6">
								<div className="flex items-start gap-3 mb-4">
									<div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center shrink-0">
										<svg className={`w-5 h-5 ${isLight ? 'text-blue-600' : 'text-blue-400'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
										</svg>
									</div>
									<div>
										<h3 className={`text-base font-semibold ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>
											{hasPassword ? 'Change password' : 'Set password'}
										</h3>
										<p className={`text-sm mt-0.5 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
											{hasPassword
												? 'Enter your current password and choose a new one.'
												: 'Set a password to enable email/password login.'}
										</p>
									</div>
								</div>

								{error && (
									<div className={`rounded-md p-2.5 mb-3 text-sm ${
										isLight
											? 'bg-red-50 text-red-700'
											: 'bg-red-900/30 text-red-300'
									}`}>
										{error}
									</div>
								)}

								<div className="space-y-3">
									{hasPassword && (
										<div>
											<label className={`block text-sm font-medium mb-1 ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>
												Current password
											</label>
											<input
												type="password"
												value={currentPassword}
												onChange={e => setCurrentPassword(e.target.value)}
												className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
													isLight
														? 'border-gray-300 bg-white text-gray-900'
														: 'border-gray-600 bg-gray-700 text-white'
												}`}
												disabled={passwordLoading}
												autoComplete="off"
											/>
										</div>
									)}
									<div>
										<label className={`block text-sm font-medium mb-1 ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>
											New password
										</label>
										<input
											type="password"
											value={newPassword}
											onChange={e => setNewPassword(e.target.value)}
											className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												isLight
													? 'border-gray-300 bg-white text-gray-900'
													: 'border-gray-600 bg-gray-700 text-white'
											}`}
											disabled={passwordLoading}
											autoComplete="off"
										/>
									</div>
									<div>
										<label className={`block text-sm font-medium mb-1 ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>
											Confirm new password
										</label>
										<input
											type="password"
											value={confirmPassword}
											onChange={e => setConfirmPassword(e.target.value)}
											className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
												isLight
													? 'border-gray-300 bg-white text-gray-900'
													: 'border-gray-600 bg-gray-700 text-white'
											}`}
											disabled={passwordLoading}
											autoComplete="off"
										/>
									</div>
								</div>
							</div>

							<div className={`px-6 py-3 flex justify-end gap-2 border-t ${isLight ? 'border-gray-200' : 'border-gray-700'}`}>
								<button
									type="button"
									onClick={resetPasswordForm}
									className={`px-3 py-2 text-sm ${isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'}`}
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={passwordLoading}
									className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
								>
									{passwordLoading ? 'Saving...' : 'Save password'}
								</button>
							</div>
						</form>
					</div>
				</div>
			)}

			{pendingAction && (
				<ReauthModal
					hasPassword={hasPassword}
					onVerified={handleReauthVerified}
					onCancel={handleReauthCancel}
				/>
			)}
		</div>
	)
}

export default SecuritySettings
